import {
  BehaviorSubject,
  catchError,
  defer,
  EMPTY,
  exhaustMap,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  of,
  retry,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
  throttleTime,
  throwError,
  timer,
} from 'rxjs';
import { AuthTypes, isIdpRefusal } from './auth.types';
import { getIdp } from './idp';

/** Wide enough to cover a slow token endpoint and a few retries before the old token lapses. */
export const REFRESH_AHEAD_MS = 2 * 60_000;
/** Between attempts while the IDP is unreachable — or sooner, the moment the browser is back online. */
export const REFRESH_RETRY_DELAY_MS = 15_000;

/** No input for this long and the user is signed out. */
export const IDLE_TIMEOUT_MS = 30 * 60_000;
/** Activity only has to be noticed, not counted — this keeps a scroll from resetting a timer per frame. */
const ACTIVITY_THROTTLE_MS = 5_000;
/** `visibilitychange` counts as activity so that returning to the tab runs the idle check. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'visibilitychange'];

/** Why a session ended without the user asking: they walked away, or the IDP refused a refresh. */
export type SessionEnd = 'idle' | 'expired';

/**
 * Holds the access token every request authenticates with, and keeps it alive.
 *
 * `services/api` reads `$accessToken` rather than a module-level variable so a request
 * issued during startup waits for the token instead of going out unauthenticated —
 * `'loading'` is a distinct state from "no token", and the client blocks on the former and
 * proceeds on the latter. `$accessToken` emits `'loading'`, then a token, then `null` once
 * the session ends; `stores/user.store` drives it.
 *
 * A started session runs two timers:
 *
 * - **Refresh**, ahead of expiry, by the refresh-token grant. Nothing in flight is touched:
 *   a request reads the token once, when it starts, so a refresh mid-request — a streamed
 *   answer included — only changes what the *next* request sends. The old token stays
 *   valid until its own expiry.
 * - **Inactivity**, which ends the session through `$sessionEnded`.
 *
 * Both are measured against the wall clock as well as a timer, because a timer does not
 * run while a laptop sleeps: waking it past either deadline has to act on that, not resume
 * counting from where it stopped.
 */
class Tokens {
  private _accessToken = new BehaviorSubject<string | 'loading' | null>('loading');
  /** The credentials the running session started from, or `null` with no session — and no timers. */
  private _session = new BehaviorSubject<AuthTypes.Credentials | null>(null);
  private _sessionEnded = new Subject<SessionEnd>();

  public $accessToken = this._accessToken.asObservable();
  /** The user store listens here; the token service does not know how to sign anyone out. */
  public $sessionEnded = this._sessionEnded.asObservable();

  constructor() {
    this._session
      .pipe(
        switchMap((credentials) =>
          credentials
            ? merge(this.$refreshes(credentials.expiresAt), $idle().pipe(map((): SessionEnd => 'idle')))
            : EMPTY
        )
      )
      .subscribe(this.end);
  }

  public get accessToken() {
    return this._accessToken.value === 'loading' ? null : this._accessToken.value;
  }

  /** Sets the token alone, with no timers. For tests and anything else that is not a session. */
  public setAccessToken = (token: string | null) => this._accessToken.next(token);

  /** Starts a session: the token, its refresh ahead of expiry, and the inactivity timer. */
  public start = (credentials: AuthTypes.Credentials) => {
    this._accessToken.next(credentials.accessToken);
    this._session.next(credentials);
  };

  public clear = () => {
    this._session.next(null);
    this._accessToken.next(null);
  };

  /** Stops the timers before telling anyone, so a session cannot end twice. */
  private end = (reason: SessionEnd) => {
    this._session.next(null);
    this._sessionEnded.next(reason);
  };

  /**
   * One refresh per token, each scheduling the next by pushing its expiry. Emits only when
   * the IDP *refuses* — which ends the session, straight away, since asking again gets the
   * same answer.
   *
   * An IDP that cannot be reached has refused nothing, so the session is kept and the
   * refresh retried until it lands; signing someone out over a dropped connection would
   * cost them a session that was still good. Once the old token lapses meanwhile, new
   * requests are held rather than sent to a certain `401`.
   */
  private $refreshes = (firstExpiry: number): Observable<SessionEnd> =>
    defer(() => {
      const expiresAt = new BehaviorSubject(firstExpiry);

      return expiresAt.pipe(
        switchMap((at) => $due(at - REFRESH_AHEAD_MS).pipe(map(() => at))),
        exhaustMap((at) => {
          /** Already expired (a long sleep): hold new requests rather than send a certain `401`. */
          const holdIfLapsed = () => {
            if (Date.now() >= at && this._accessToken.value !== 'loading') this._accessToken.next('loading');
          };
          holdIfLapsed();

          return defer(() => getIdp().then((idp) => idp.refresh())).pipe(
            retry({
              delay: (err) => {
                if (isIdpRefusal(err)) return throwError(() => err);
                holdIfLapsed();
                return merge(timer(REFRESH_RETRY_DELAY_MS), fromEvent(window, 'online')).pipe(take(1));
              },
            }),
            tap((credentials) => {
              this._accessToken.next(credentials.accessToken);
              expiresAt.next(credentials.expiresAt);
            }),
            switchMap(() => EMPTY),
            catchError(() => of<SessionEnd>('expired'))
          );
        })
      );
    });
}

/** Fires once at `at`, or as soon as the tab is visible again after it. */
const $due = (at: number) =>
  merge(
    timer(Math.max(0, at - Date.now())),
    fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible' && Date.now() >= at)
    )
  ).pipe(take(1));

/**
 * Emits once the user has been inactive for `IDLE_TIMEOUT_MS`. Each burst of activity
 * restarts the countdown — unless the gap before it already exceeded the timeout, which is
 * what a timer that was frozen by sleep would otherwise miss.
 */
const $idle = () =>
  defer(() => {
    let lastActiveAt = Date.now();

    return merge(...ACTIVITY_EVENTS.map((type) => fromEvent(document, type, { passive: true }))).pipe(
      throttleTime(ACTIVITY_THROTTLE_MS),
      startWith(null),
      switchMap(() => {
        if (Date.now() - lastActiveAt >= IDLE_TIMEOUT_MS) return of(null);
        lastActiveAt = Date.now();
        return timer(IDLE_TIMEOUT_MS);
      }),
      take(1)
    );
  });

export const tokens = Object.freeze(new Tokens());
